import { Button, IconButton, Modal, Fade, Box, CircularProgress, Typography, Paper, Divider } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { WorkAreaList } from '../../project/edit/DFRConfiguration/WorkAreaList'
import CloseIcon from '@mui/icons-material/Close'
import { WorkArea } from '../../../types/Project/WorkArea'
import { AreaSelectCanvas } from './AreaSelectCanvas'

type WorkAreaSelectionModalProps = {
	workAreas: WorkArea[]
	imageUrl: string
	setSelected: (area: WorkArea) => void
	selected: string | null
	error: boolean | undefined
}

export const WorkAreaSelectionModal = ({
	workAreas,
	imageUrl,
	setSelected,
	selected,
	error,
}: WorkAreaSelectionModalProps) => {
	const [open, setOpen] = useState(false)

	function areaSelected(workArea: WorkArea): void {
		setSelected(workArea)
		setOpen(false)
	}

	return (
		<>
			<Button
				variant='outlined'
				sx={{
					color: 'primary.main',
					textDecoration: 'underline',
					borderColor: error ? 'error.main' : 'primary.main',
					border: error ? '2px solid red' : '1px transparent',
					borderRadius: '8px',
					textTransform: 'none',
					fontWeight: 600,
					'&:hover': {
						backgroundColor: 'rgba(25, 118, 210, 0.04)',
					}
				}}
				onClick={() => setOpen(true)}
			>
				{selected ? selected : 'Choose a location'}
			</Button>

			<Modal open={open} onClose={() => setOpen(false)}>
				<Fade in={open}>
					<Box
						sx={{
							position: 'absolute',
							bgcolor: 'background.paper',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
							width: '95vw',
							height: '95vh',
							borderRadius: '12px',
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								p: 3,
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
							}}
						>
							<Box sx={{ 
								display: 'flex', 
								justifyContent: 'space-between',
								alignItems: 'center',
								mb: 2,
								flexShrink: 0
							}}>
								<Typography 
									variant="h5" 
									sx={{ 
										fontWeight: 600,
										color: 'text.primary'
									}}
								>
									Select Work Area
								</Typography>
								<IconButton
									color='error'
									sx={{ 
										width: '40px',
										height: '40px',
										borderRadius: '8px',
										backgroundColor: 'error.light',
										'&:hover': {
											backgroundColor: 'error.main',
										}
									}}
									onClick={() => setOpen(false)}
								>
									<CloseIcon />
								</IconButton>
							</Box>

							<Divider sx={{ mt: 1, flexShrink: 0 }} />

							<Box 
								sx={{ 
									display: 'flex', 
									flex: 1,
									gap: 3,
									overflow: 'hidden',
									minHeight: 0
								}}
							>
								<Paper 
									elevation={0}
									sx={{ 
										flex: 1,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										borderRadius: '8px',
										overflow: 'hidden',
										backgroundColor: 'background.default',
										border: '1px solid',
										borderColor: 'divider',
										minHeight: 0
									}}
								>
									<AreaSelectCanvas
										workAreas={workAreas}
										imageUrl={imageUrl}
										setSelected={function (area: WorkArea): void {
											areaSelected(area)
										}}
									/>
								</Paper>
								<Paper
									elevation={0}
									sx={{
										width: '300px',
										borderRadius: '8px',
										border: '1px solid',
										borderColor: 'divider',
										overflow: 'hidden',
										minHeight: 0
									}}
								>
									<WorkAreaList
										workAreas={workAreas}
										setSelected={(workArea) => areaSelected(workArea)}
									/>
								</Paper>
							</Box>
						</Box>
					</Box>
				</Fade>
			</Modal>
		</>
	)
}
